import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Building2, Plus, Users, Wrench, FileText, Store, LogOut, Pause, Play, Pencil, ShieldCheck, PackageOpen, FlaskConical, Handshake, HousePlus, Calendar, ChevronDown, ChevronUp, ScrollText, Receipt, FolderArchive, Inbox, Landmark } from "lucide-react";
import TabListino from "./superadmin/TabListino";
import SuperAdminTrial from "../components/SuperAdminTrial";
import SuperAdminOnboarding from "../components/SuperAdminOnboarding";
import SuperAdminCondominiRichieste from "../components/SuperAdminCondominiRichieste";
import SuperAdminRinnoviAnnuali from "../components/SuperAdminRinnoviAnnuali";
import SuperAdminContrattiModelli from "../components/SuperAdminContrattiModelli";
import SuperAdminFatture from "../components/SuperAdminFatture";
import SuperAdminReports from "../components/SuperAdminReports";
import SuperAdminBonifico from "../components/SuperAdminBonifico";
import SuperAdminLicenzeCondomini from "../components/SuperAdminLicenzeCondomini";
import SuperAdminRichiesteCondomini from "../components/SuperAdminRichiesteCondomini";
import GeneraContrattoButton from "../components/GeneraContrattoButton";
import SuperAdminEmailSender from "../components/SuperAdminEmailSender";
import SuperAdminDocumentazione from "../components/SuperAdminDocumentazione";

const EMPTY = { nome: "", report_email: "", admin_nome: "", admin_email: "", admin_password: "", fornitore_ref_id: null };

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  // Iter122cd · /superadmin è riservato esclusivamente al ruolo super_admin (SuperProtected in App.js).
  // Le tab Trial/Onboarding sono state rimosse dall'accesso admin Izzo su richiesta.
  const isSuperOnly = user?.role === "super_admin";
  const [tab, setTab] = useState("studi");
  const [catSezioni, setCatSezioni] = useState([]);
  const [richiestaSeed, setRichiestaSeed] = useState(null);
  const [expandedBilling, setExpandedBilling] = useState({});
  const [expandedCard, setExpandedCard] = useState({});   // Iter122bw · card compatta con menu a tendina
  // Iter122bl · richiamo da anagrafica fornitori
  const [anagStudi, setAnagStudi] = useState([]);
  const [anagSearch, setAnagSearch] = useState("");
  const [anagLoading, setAnagLoading] = useState(false);
  const [anagOpen, setAnagOpen] = useState(false);
  // Iter122bq · sempre filtrato: solo fornitori di categoria "Amministrazione Condòmini" / "Amministratori"

  const cercaAnagrafica = async () => {
    if (!isSuperOnly) return;
    setAnagLoading(true);
    try {
      const { data } = await api.get("/superadmin/anagrafica-studi", {
        params: anagSearch ? { search: anagSearch } : {},
      });
      setAnagStudi(data || []);
    } catch (e) { toast.error("Caricamento anagrafica non riuscito"); }
    setAnagLoading(false);
  };

  const pickFromAnagrafica = (f) => {
    if (f.tenant_collegato) {
      toast.error(`Questo studio è già collegato al tenant "${f.tenant_collegato.nome}"`);
      return;
    }
    const nomeCompleto = f.ragione_sociale || f.nome || "";
    const emailStudio = (f.email || f.pec || "").toLowerCase();
    setForm({
      ...form,
      nome: nomeCompleto,
      report_email: emailStudio,
      admin_email: emailStudio,
      admin_nome: f.referente || nomeCompleto,
      fornitore_ref_id: f.id,
    });
    setAnagOpen(false);
    toast.success(`"${nomeCompleto}" richiamato dall'anagrafica · verifica e inserisci password`);
  };

  const load = async () => {
    if (!isSuperOnly) return;  // admin Izzo non ha permessi su /superadmin/tenants
    try {
      const { data } = await api.get("/superadmin/tenants");
      setTenants(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore di caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!isSuperOnly) return;
    api.get("/superadmin/sezioni-opzionali").then((r) => setCatSezioni(r.data)).catch(() => {});
  }, [isSuperOnly]);

  const crea = async () => {
    if (!form.nome.trim() || !form.report_email || !form.admin_email || !form.admin_password) {
      toast.error("Compila tutti i campi obbligatori"); return;
    }
    setBusy(true);
    try {
      await api.post("/superadmin/tenants", form);
      toast.success("Studio creato: l'amministratore può già accedere con le credenziali indicate.");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Creazione non riuscita"); }
    finally { setBusy(false); }
  };

  const toggleStato = async (t) => {
    const sospendi = t.stato === "attivo";
    if (!window.confirm(sospendi
      ? `Sospendere "${t.nome}"? Tutti i suoi utenti perderanno immediatamente l'accesso.`
      : `Riattivare "${t.nome}"?`)) return;
    try {
      await api.post(`/superadmin/tenants/${t.id}/${sospendi ? "sospendi" : "riattiva"}`);
      toast.success(sospendi ? "Studio sospeso" : "Studio riattivato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
  };

  const salvaEdit = async () => {
    setBusy(true);
    try {
      let logo_file_id = edit.branding.logo_file_id;
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const { data } = await api.post("/uploads/documento", fd, { headers: { "Content-Type": "multipart/form-data" } });
        logo_file_id = data.id;
      }
      await api.put(`/superadmin/tenants/${edit.id}`, {
        nome: edit.nome, report_email: edit.report_email,
        branding: { ...edit.branding, logo_file_id },
        sezioni_disabilitate: edit.sezioni_disabilitate || [],
      });
      toast.success("Studio aggiornato"); setEdit(null); setLogoFile(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aggiornamento non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="superadmin-page">
      <header className="bg-brand text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <ShieldCheck className="w-8 h-8" />
          <div>
            <h1 className="font-display text-2xl font-bold leading-tight">Ginius — Super Admin</h1>
            <p className="text-sm text-white/70">Gestione degli studi sulla piattaforma</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:block text-sm text-white/80">{user?.name}</span>
            <Button data-testid="superadmin-logout" variant="outline" onClick={logout}
              className="h-10 bg-transparent border-white/40 text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Tab bar · Iter122ba · mobile: swipe orizzontale, voci in linea singola */}
        <div className="flex gap-2 border-b border-slate-200 mb-6 overflow-x-auto flex-nowrap -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin" data-testid="superadmin-tabs">
          {[
            ...(isSuperOnly ? [
              { k: "studi", label: "Studi", icon: Building2 },
              { k: "listino", label: "Piani & Licenze", icon: PackageOpen },
              { k: "trial", label: "Trial attivi", icon: FlaskConical },
              { k: "onboarding", label: "Onboarding studi", icon: Handshake },
              { k: "rinnovi-annuali", label: "Rinnovi annuali", icon: Calendar },
              { k: "contratti-modelli", label: "Modelli contratti", icon: ScrollText },
              { k: "fatture", label: "Fatture & Pagamenti", icon: Receipt },
              { k: "reports", label: "Report interni", icon: FolderArchive },
              { k: "bonifico", label: "Estremi bonifico", icon: Landmark },
              { k: "richieste-lic-cond", label: "Richieste licenze condomìni", icon: Inbox },
            ] : []),
          ].map((t) => {
            const Ic = t.icon;
            const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                      className={`shrink-0 whitespace-nowrap flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-t-lg border-b-2 transition-colors
                        ${active ? "border-brand text-brand bg-white" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <Ic className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "studi" && (
          <>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="font-display text-3xl font-bold text-slate-800">Studi ({tenants?.length ?? "…"})</h2>
          <Button data-testid="new-tenant-button" onClick={() => setOpen(true)} className="h-12 bg-brand hover:bg-brand-dark">
            <Plus className="w-5 h-5 mr-2" /> Nuovo studio
          </Button>
        </div>

        {tenants === null ? (
          <p className="text-slate-500">Caricamento...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tenants.map((t) => (
              <Card key={t.id} className="p-3 sm:p-4 border-2 min-w-0 w-full" data-testid={`tenant-card-${t.id}`}>
                {/* Iter122bw · Card compatta: nome + stato + modifica + chevron per espandere il resto */}
                <div className="flex items-center justify-between gap-2">
                  <button type="button"
                          onClick={() => setExpandedCard((s) => ({ ...s, [t.id]: !s[t.id] }))}
                          data-testid={`tenant-expand-${t.id}`}
                          className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-brand" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-sm sm:text-base font-bold truncate">{t.nome}</h3>
                      <p className="text-[10px] sm:text-xs text-slate-500 truncate">{t.report_email || t.id}</p>
                    </div>
                    <span data-testid={`tenant-stato-${t.id}`}
                      className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold border shrink-0 ${t.stato === "attivo" ? "bg-green-100 text-green-800 border-green-300" : "bg-red-100 text-red-800 border-red-300"}`}>
                      {t.stato === "attivo" ? "Attivo" : "Sospeso"}
                    </span>
                  </button>
                  <Button variant="outline" size="sm" className="h-8 sm:h-9 px-2 sm:px-3 shrink-0" data-testid={`tenant-edit-${t.id}`}
                    onClick={() => { setLogoFile(null); setEdit({ id: t.id, nome: t.nome, report_email: t.report_email || "", sezioni_disabilitate: t.sezioni_disabilitate || [], branding: { nome_display: "", colore: "#7A193D", colore_scuro: "#5C112C", slogan: "", indirizzo: "", contatti: "", telefono: "", logo_file_id: null, ...(t.branding || {}) } }); }}>
                    <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" /> <span className="hidden sm:inline">Modifica</span>
                  </Button>
                  <button type="button" onClick={() => setExpandedCard((s) => ({ ...s, [t.id]: !s[t.id] }))}
                          className="shrink-0 p-1.5 hover:bg-slate-100 rounded" aria-label="Espandi">
                    {expandedCard[t.id] ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </button>
                </div>

                {expandedCard[t.id] && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                  {[["utenti", Users], ["condomini", Building2], ["fornitori", Store], ["manutenzioni", Wrench], ["preventivi", FileText]].map(([k, Icon]) => (
                    <div key={k} className="min-w-0 rounded-lg bg-slate-50 border border-slate-200 py-2 px-1">
                      <Icon className="w-4 h-4 mx-auto text-slate-400" />
                      <div className="text-lg font-bold text-slate-800 truncate">{t.stats?.[k] ?? 0}</div>
                      <div className="text-[10px] uppercase text-slate-500 truncate">{k === "condomini" ? "condomìni" : k}</div>
                    </div>
                  ))}
                </div>
                {/* Iter122bj · pannello espandibile richieste licenze + rinnovi per lo studio */}
                {t.id !== "studio" && (
                  <div className="mt-4">
                    <button
                      type="button"
                      data-testid={`tenant-billing-toggle-${t.id}`}
                      onClick={() => setExpandedBilling((s) => ({ ...s, [t.id]: !s[t.id] }))}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border-2 border-slate-200 hover:bg-slate-50 text-sm font-bold text-slate-700 transition-colors">
                      <span className="inline-flex items-center gap-2">
                        <HousePlus className="w-4 h-4 text-brand" />
                        Richieste licenze &amp; rinnovi
                      </span>
                      {expandedBilling[t.id] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {expandedBilling[t.id] && (
                      <div className="mt-3 space-y-4 border-2 border-slate-100 rounded-lg p-3" data-testid={`tenant-billing-panel-${t.id}`}>
                        <div>
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                            <HousePlus className="w-3.5 h-3.5" /> Richieste aggiunta condomìni
                          </div>
                          <SuperAdminCondominiRichieste tenantId={t.id} compact />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> Rinnovi annuali dello studio
                          </div>
                          <SuperAdminRinnoviAnnuali tenantId={t.id} compact />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="actions-row mt-4 flex flex-wrap gap-2">
                  {t.id !== "studio" && (
                    <Button variant="outline" data-testid={`tenant-toggle-${t.id}`}
                      className={`h-10 ${t.stato === "attivo" ? "text-red-700 border-red-300" : "text-green-700 border-green-400"}`}
                      onClick={() => toggleStato(t)}>
                      {t.stato === "attivo" ? <><Pause className="w-4 h-4 mr-1" /> Sospendi</> : <><Play className="w-4 h-4 mr-1" /> Riattiva</>}
                    </Button>
                  )}
                  {/* Iter122bm · Genera contratto */}
                  {t.id !== "studio" && (
                    <GeneraContrattoButton tenant={t} />
                  )}
                </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
        {/* Mittenti email @giniusapp.it · verifica dominio + panoramica alias */}
        <SuperAdminEmailSender />
          </>
        )}

        {tab === "listino" && (
          <TabListino tenants={tenants || []} onReload={load} />
        )}

        {/* Iter122bb · Trial nativo in SuperAdmin */}
        {tab === "trial" && (
          <div className="rounded-lg" data-testid="superadmin-tab-trial">
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical className="w-6 h-6 text-brand" />
              <h2 className="font-display text-2xl font-bold text-slate-800">Account di prova (Trial)</h2>
            </div>
            <SuperAdminTrial
              richiestaSeed={richiestaSeed}
              onTrialCreated={() => setRichiestaSeed(null)}
            />
          </div>
        )}

        {/* Iter122bj · sezione "Richieste condomìni" rimossa dalle tab: ora è embedded nella card studio. */}

        {/* Iter122bi · Rinnovi annuali · storico note ricorrenti aggregate su tutti gli studi */}
        {tab === "rinnovi-annuali" && (
          <div className="rounded-lg" data-testid="superadmin-tab-rinnovi-annuali">
            <SuperAdminRinnoviAnnuali />
          </div>
        )}

        {/* Iter122bm · Modelli di contratto (editabili) */}
        {tab === "contratti-modelli" && (
          <div className="rounded-lg" data-testid="superadmin-tab-contratti-modelli">
            <SuperAdminContrattiModelli />
          </div>
        )}

        {/* Iter122bz · Fatture & Pagamenti */}
        {tab === "fatture" && (
          <div className="rounded-lg" data-testid="superadmin-tab-fatture">
            <SuperAdminFatture />
          </div>
        )}

        {/* Iter122ce · Report interni · Iter122fd · include documentazione tecnica */}
        {tab === "reports" && (
          <div className="rounded-lg space-y-6" data-testid="superadmin-tab-reports">
            <SuperAdminReports />
            <SuperAdminDocumentazione />
          </div>
        )}

        {/* Iter122du · Estremi bonifico (pagina pubblica /piani) */}
        {tab === "bonifico" && (
          <div className="rounded-lg" data-testid="superadmin-tab-bonifico">
            <SuperAdminBonifico />
          </div>
        )}

        {/* Iter122ck · Richieste licenze condomìni · Iter122dx · Fase 4 aggiunto pannello attivazione */}
        {tab === "richieste-lic-cond" && (
          <div className="rounded-lg space-y-6" data-testid="superadmin-tab-richieste-lc">
            <SuperAdminLicenzeCondomini />
            <div className="border-t pt-6">
              <SuperAdminRichiesteCondomini />
            </div>
          </div>
        )}

        {/* Iter122bb · Onboarding studi (Richieste + Outreach unificati) */}
        {tab === "onboarding" && (
          <div className="rounded-lg" data-testid="superadmin-tab-onboarding">
            <div className="flex items-center gap-2 mb-4">
              <Handshake className="w-6 h-6 text-brand" />
              <h2 className="font-display text-2xl font-bold text-slate-800">Onboarding studi</h2>
            </div>
            <SuperAdminOnboarding
              onCreaProvaDaRichiesta={(r) => { setRichiestaSeed(r); setTab("trial"); }}
            />
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAnagOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo studio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Iter122bl · richiamo studio da Anagrafica Fornitori Izzo */}
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-brand/5 border-2 border-brand/20">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-bold text-slate-800">Vuoi richiamare uno studio già in anagrafica?</p>
                <p className="text-xs text-slate-500">Pre-compila nome, email e dati dallo Studio Amministrazione presente nei Fornitori.</p>
              </div>
              <Button size="sm" variant="outline" data-testid="tenant-picker-open" onClick={() => { setAnagOpen(true); if (anagStudi.length === 0) cercaAnagrafica(); }} className="h-9 border-brand text-brand hover:bg-brand hover:text-white">
                <Handshake className="w-4 h-4 mr-1" /> Richiama da Anagrafica
              </Button>
            </div>
            {form.fornitore_ref_id && (
              <div className="p-2 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 font-semibold flex items-center justify-between">
                <span>✓ Studio collegato all&apos;anagrafica fornitori (id: {form.fornitore_ref_id.slice(0, 8)}…)</span>
                <button type="button" className="text-emerald-700 underline" onClick={() => setForm({ ...form, fornitore_ref_id: null })}>rimuovi collegamento</button>
              </div>
            )}
            <div><Label>Nome studio *</Label><Input data-testid="tenant-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="h-11 mt-1" placeholder="Es. Studio Rossi Amministrazioni" /></div>
            <div><Label>Email notifiche/report *</Label><Input data-testid="tenant-report-email" type="email" value={form.report_email} onChange={(e) => setForm({ ...form, report_email: e.target.value })} className="h-11 mt-1" /></div>
            <div className="pt-2 border-t">
              <p className="text-sm font-bold text-slate-600 mb-2">Primo account amministratore</p>
              <div className="space-y-3">
                <div><Label>Nome e cognome *</Label><Input data-testid="tenant-admin-nome" value={form.admin_nome} onChange={(e) => setForm({ ...form, admin_nome: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Email *</Label><Input data-testid="tenant-admin-email" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Password * (min 8 caratteri)</Label><Input data-testid="tenant-admin-password" type="text" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="h-11 mt-1" /></div>
              </div>
            </div>
            <div className="actions-row pt-2">
              <Button data-testid="tenant-create-confirm" onClick={crea} disabled={busy} className="h-11 bg-brand hover:bg-brand-dark">
                {busy ? "Creazione..." : "Crea studio"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setOpen(false)}>Annulla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter122bl · picker anagrafica · Iter122ei · centering fix (rimosso w-[95vw] ridondante) */}
      <Dialog open={anagOpen} onOpenChange={setAnagOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-lg sm:text-xl">Richiama da Anagrafica Fornitori</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
              <Input placeholder="Cerca per nome, città, email…" value={anagSearch} onChange={(e) => setAnagSearch(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && cercaAnagrafica()}
                     data-testid="tenant-picker-search" className="h-10 w-full sm:flex-1 sm:min-w-[180px]" />
              <Button size="sm" onClick={cercaAnagrafica} disabled={anagLoading} data-testid="tenant-picker-cerca" className="h-10 bg-brand hover:bg-brand-dark whitespace-nowrap">
                {anagLoading ? "…" : "Cerca"}
              </Button>
            </div>
            <p className="text-xs text-slate-500 break-words">
              Vengono mostrati solo i fornitori di categoria <b>Amministrazione Condòmini</b> / <b>Amministratori</b>. Se non trovi lo studio: aprilo in Fornitori e aggiungi la categoria corretta.
            </p>
            {anagStudi.length === 0 && !anagLoading && <div className="p-4 text-center text-slate-400 text-sm border rounded-lg">Nessuno studio di categoria &quot;Amministrazione Condòmini&quot; trovato. Aggiungi la categoria al fornitore in anagrafica.</div>}
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {anagStudi.map((f) => {
                const collegato = !!f.tenant_collegato;
                return (
                  <div key={f.id} className={`p-3 rounded-lg border-2 ${collegato ? "opacity-60 bg-slate-50" : "hover:bg-brand/5 hover:border-brand cursor-pointer"} transition-colors`}
                       data-testid={`anag-item-${f.id}`}
                       onClick={() => !collegato && pickFromAnagrafica(f)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-800 truncate">{f.ragione_sociale || f.nome || "(senza nome)"}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {(f.categorie || []).join(" · ") || "—"}{f.citta && ` · ${f.citta}${f.provincia ? ` (${f.provincia})` : ""}`}
                        </div>
                        <div className="text-xs text-slate-400 break-all">
                          {f.email && <span>✉ {f.email}</span>}{f.telefono && <span className="ml-3 whitespace-nowrap">☎ {f.telefono}</span>}{f.partita_iva && <span className="ml-3 whitespace-nowrap">P.IVA {f.partita_iva}</span>}
                        </div>
                      </div>
                      {collegato ? (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 whitespace-nowrap flex-shrink-0">già collegato</span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-8 whitespace-nowrap flex-shrink-0" onClick={(e) => { e.stopPropagation(); pickFromAnagrafica(f); }} data-testid={`anag-pick-${f.id}`}>Seleziona</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Modifica studio</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Nome studio</Label><Input data-testid="tenant-edit-nome" value={edit.nome} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>Email notifiche/report</Label><Input data-testid="tenant-edit-email" type="email" value={edit.report_email} onChange={(e) => setEdit({ ...edit, report_email: e.target.value })} className="h-11 mt-1" /></div>
              {edit.id !== "studio" && (
                <div className="pt-2 border-t space-y-3">
                  <div>
                    <p className="text-sm font-bold text-slate-600">Sezioni & Moduli abilitati</p>
                    <p className="text-xs text-slate-500 mb-2">Deseleziona le sezioni che <b>non</b> vuoi rendere disponibili a questo studio (es. moduli non ancora contrattualizzati o in beta).</p>
                    <div className="space-y-2">
                      {(catSezioni || []).map((s) => {
                        const disabilitata = (edit.sezioni_disabilitate || []).includes(s.key);
                        return (
                          <label key={s.key} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                                 data-testid={`sezione-toggle-${s.key}`}>
                            <input type="checkbox" checked={!disabilitata}
                                   onChange={(e) => {
                                     const cur = new Set(edit.sezioni_disabilitate || []);
                                     if (e.target.checked) cur.delete(s.key); else cur.add(s.key);
                                     setEdit({ ...edit, sezioni_disabilitate: Array.from(cur) });
                                   }}
                                   className="h-5 w-5 mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-bold text-slate-800">{s.label}</div>
                              {s.descrizione && <div className="text-xs text-slate-500">{s.descrizione}</div>}
                            </div>
                            <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded self-center ${disabilitata ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                              {disabilitata ? "DISATTIVA" : "ATTIVA"}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {edit.id !== "studio" && (
                <div className="pt-2 border-t space-y-3">
                  <p className="text-sm font-bold text-slate-600">Branding (white-label)</p>
                  <div><Label>Nome visualizzato nell&apos;app</Label><Input data-testid="branding-nome" value={edit.branding.nome_display || ""} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, nome_display: e.target.value } })} className="h-11 mt-1" placeholder={edit.nome} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Colore primario</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" data-testid="branding-colore" value={edit.branding.colore || "#7A193D"} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, colore: e.target.value } })} className="h-11 w-14 rounded border cursor-pointer" />
                        <span className="text-sm font-mono">{edit.branding.colore || "#7A193D"}</span>
                      </div>
                    </div>
                    <div><Label>Colore scuro (hover)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" data-testid="branding-colore-scuro" value={edit.branding.colore_scuro || "#5C112C"} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, colore_scuro: e.target.value } })} className="h-11 w-14 rounded border cursor-pointer" />
                        <span className="text-sm font-mono">{edit.branding.colore_scuro || "#5C112C"}</span>
                      </div>
                    </div>
                  </div>
                  <div><Label>Slogan</Label><Input data-testid="branding-slogan" value={edit.branding.slogan || ""} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, slogan: e.target.value } })} className="h-11 mt-1" /></div>
                  <div><Label>Indirizzo (intestazione PDF)</Label><Input data-testid="branding-indirizzo" value={edit.branding.indirizzo || ""} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, indirizzo: e.target.value } })} className="h-11 mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email contatti</Label><Input data-testid="branding-contatti" value={edit.branding.contatti || ""} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, contatti: e.target.value } })} className="h-11 mt-1" /></div>
                    <div><Label>Telefono</Label><Input data-testid="branding-telefono" value={edit.branding.telefono || ""} onChange={(e) => setEdit({ ...edit, branding: { ...edit.branding, telefono: e.target.value } })} className="h-11 mt-1" /></div>
                  </div>
                  <div><Label>Logo (PNG/JPG)</Label>
                    <input type="file" accept="image/*" data-testid="branding-logo-file" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
                    {edit.branding.logo_file_id && !logoFile && <p className="text-xs text-green-700 mt-1">Logo già caricato ✓</p>}
                  </div>
                </div>
              )}
              <div className="actions-row pt-2">
                <Button data-testid="tenant-edit-save" onClick={salvaEdit} disabled={busy} className="h-11 bg-brand hover:bg-brand-dark">{busy ? "Salvataggio..." : "Salva"}</Button>
                <Button variant="outline" className="h-11" onClick={() => setEdit(null)}>Annulla</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
